import { useState, useEffect, useRef, useCallback } from "react";
import { 
  TrendingUp,
  DollarSign, 
  Headphones, 
  Zap,
  Globe2,
  ChevronRight,
  ChevronLeft,
  Lock,
  CreditCard,
  Gauge,
  Users,
  ArrowUpRight,
  Gift,
  Shield,
  Rocket,
  Target,
  Clock,
  RefreshCw,
  Building2,
  LineChart,
  CheckCircle2,
  Infinity,
} from "lucide-react";
import "../components/css/style.css";

const cards = [
  {
    id: 3,
    title: "Zero Adspend Fees",
    desc: "Our focus is on helping you scale, not slashing your margins. Pay $0 in platform fees on your adspend.",
    badge: "↓%",
    badgeLabel: "Earn cashbacks",
    badgePrefix: "",
    accent: "#149fa2",
    icon: DollarSign,
  },
  {
    id: 4,
    title: "24/7 Expert Support",
    desc: "Get dedicated account managers and real-time support whenever you need it.",
    badge: "24/7",
    badgeLabel: "Live support",
    badgePrefix: "",
    accent: "#149fa2",
    icon: Headphones,
  },
  {
    id: 5,
    title: "Instant Approval",
    desc: "Skip the waiting period. Get your advertising accounts approved within 48-120 hours",
    badgeLabel: "Within 48-120 hours",
    badgePrefix: "",
    accent: "#149fa2",
    icon: Zap,
  },
  {
    id: 6,
    title: "Global Reach",
    desc: "Access audiences worldwide with localized ad delivery and targeting across 200+ countries.",
    badgeLabel: "200+ countries",
    badgePrefix: "",
    accent: "#149fa2",
    icon: Globe2,
  },
  {
    id: 7,
    title: "High Daily Limits",
    desc: "Start scaling immediately with $500-1500+ daily spend limits on fresh accounts. No more waiting for limits to increase.",
    badge: "$1,500+",
    badgeLabel: "Daily spend limit",
    badgePrefix: "",
    accent: "#149fa2",
    icon: Rocket,
  },
  {
    id: 8,
    title: "Agency Ad Account Perks",
    desc: "Access exclusive agency-level features: advanced reporting, beta features, and priority review for ad disapprovals.",
    badge: "Agency",
    badgeLabel: "Exclusive access",
    badgePrefix: "",
    accent: "#149fa2",
    icon: Building2,
  },
  {
    id: 9,
    title: "Ban Protection",
    desc: "Reduced risk of account bans with our whitelisted status. Get account reinstatement support within 24 hours.",
    badge: "99.9%",
    badgeLabel: "Account uptime",
    badgePrefix: "",
    accent: "#149fa2",
    icon: Shield,
  },
  {
    id: 10,
    title: "Cashback Rewards",
    desc: "Earn up to 2% cashback on your yearly adspend. The more you scale, the more you earn back.",
    badge: "2%",
    badgeLabel: "Cashback on spend",
    badgePrefix: "",
    accent: "#149fa2",
    icon: Gift,
  },
  {
    id: 12,
    title: "Advanced Analytics",
    desc: "Get deeper insights with custom dashboards, cross-account attribution, and real-time performance alerts.",
    badge: "Real-time data",
    badgeLabel: "Analytics",
    badgePrefix: "",
    accent: "#149fa2",
    icon: LineChart,
  },
  {
    id: 13,
    title: "Automated Rules Engine",
    desc: "Set smart automation rules to pause underperforming ads, scale winning campaigns, and protect your budget.",
    badge: "24/7",
    badgeLabel: "Auto-optimization",
    badgePrefix: "",
    accent: "#149fa2",
    icon: RefreshCw,
  },
  {
    id: 15,
    title: "Infinite Scaling",
    desc: "No spending caps, no daily limits, no nonsense. Scale your campaigns as fast as your budget allows.",
    badge: "∞",
    badgeLabel: "Unlimited scaling",
    badgePrefix: "",
    accent: "#149fa2",
    icon: Infinity,
  },
  {
    id: 16,
    title: "Conversion API Setup",
    txt: "Get expert setup of Meta Conversion API for better tracking, higher event match quality, and improved performance.",
    badge: "CAPI",
    badgeLabel: "Expert setup",
    badgePrefix: "",
    accent: "#149fa2",
    icon: Target,
  }
];

const adMockups = [
  {
    id: 1,
    brand: "Warefare store",
    img: "https://images.unsplash.com/photo-1529139574466-a303027c1d8b?w=300&q=80",
    type: "fashion",
    delay: 0,
  },
  {
    id: 2,
    brand: "Brightlit",
    img: "https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=300&q=80",
    type: "lifestyle",
    delay: 150,
  },
  {
    id: 3,
    brand: "Shop now, pay later.",
    img: "https://images.unsplash.com/photo-1542291026-7eec264c27ff?w=300&q=80",
    type: "product",
    delay: 300,
    cta: "Let's go",
  },
  {
    id: 4,
    brand: "Urban Wear",
    img: "https://images.unsplash.com/photo-1441986300917-64674bd600d8?w=300&q=80",
    type: "fashion",
    delay: 450,
  },
  {
    id: 5,
    brand: "TechFlow",
    img: "https://images.unsplash.com/photo-1550745165-9bc0b252726f?w=300&q=80",
    type: "tech",
    delay: 600,
    cta: "Learn more",
  },
];

function AdMockupCard({ mock, index }) {
  const [visible, setVisible] = useState(false);
  const cardRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setTimeout(() => setVisible(true), mock.delay);
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );

    if (cardRef.current) {
      observer.observe(cardRef.current);
    }

    return () => observer.disconnect();
  }, [mock.delay]);

  return (
    <div ref={cardRef} className={`ad-agency-mockup-card ${visible ? "visible" : ""}`}>
      <img
        src={mock.img}
        alt={mock.brand}
        className="ad-agency-mockup-image"
      />
      <div className="ad-agency-mockup-content">
        <div className="ad-agency-mockup-brand">{mock.brand}</div>
        {mock.cta && (
          <button className="ad-agency-mockup-button">
            {mock.cta} <ArrowUpRight size={12} />
          </button>
        )}
      </div>
    </div>
  );
}

function FeatureCard({ card, index, isVisible }) {
  const [visible, setVisible] = useState(false);
  const cardRef = useRef(null);
  const Icon = card.icon;

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );

    if (cardRef.current) {
      observer.observe(cardRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <div ref={cardRef} className={`ad-agency-feature-card ${visible ? "visible" : ""} ${card.popular ? "popular" : ""}`}>
      <div className="ad-agency-feature-border" />
      
      {card.metric && (
        <div className="ad-agency-feature-metric">
          {card.metric}
        </div>
      )}
      
      <div className="ad-agency-feature-icon-wrapper">
        <Icon size={24} color="#149fa2" />
      </div>
      <div className="ad-agency-feature-title">{card.title}</div>
      <div className="ad-agency-feature-desc">{card.desc}</div>

      {card.id === 3 && (
        <div className="ad-agency-feature-badge ad-agency-zero-fee">
          <div className="ad-agency-zero-icon">$</div>
          <div className="ad-agency-zero-text">
            <span className="ad-agency-badge-label">{card.badgeLabel}</span>
          </div>
        </div>
      )}

      {card.id === 4 && (
        <div className="ad-agency-feature-badge ad-agency-support-badge">
          <div className="ad-agency-support-avatars">
            <div className="ad-agency-avatar">JD</div>
            <div className="ad-agency-avatar">MK</div>
            <div className="ad-agency-avatar">+2</div>
          </div>
          <span className="ad-agency-badge-text">Dedicated account manager</span>
        </div>
      )}

      {card.id === 5 && (
        <div className="ad-agency-feature-badge ad-agency-timer-badge">
          <Clock size={18} color="#ffffff"/>
          <span className="ad-agency-badge-text">Average approval: 48 hours</span>
        </div>
      )}

      {card.id === 6 && (
        <div className="ad-agency-feature-badge ad-agency-globe-badge">
          <Globe2 size={18} color="#ffffff"/>
          <span className="ad-agency-badge-text">{card.badgeLabel}</span>
        </div>
      )}

      {card.id === 7 && (
        <div className="ad-agency-feature-badge ad-agency-limit-badge">
          <Rocket size={18} color="#ffffff"/>
          <div className="ad-agency-limit-info">
            <span className="ad-agency-badge-value">{card.badge}</span>
            <span className="ad-agency-badge-label">{card.badgeLabel}</span>
          </div>
        </div>
      )}

      {card.id === 8 && (
        <div className="ad-agency-feature-badge ad-agency-agency-badge">
          <Building2 size={18} color="#ffffff"/>
          <span className="ad-agency-badge-text">Agency-level features unlocked</span>
        </div>
      )}

      {card.id === 9 && (
        <div className="ad-agency-feature-badge ad-agency-protection-badge">
          <Shield size={18} color="#ffffff"/>
          <div className="ad-agency-protection-info">
            <span className="ad-agency-badge-value">{card.badge}</span>
            <span className="ad-agency-badge-label">{card.badgeLabel}</span>
          </div>
        </div>
      )}

      {card.id === 10 && (
        <div className="ad-agency-feature-badge ad-agency-cashback-badge-new">
          <Gift size={18} color="#ffffff"/>
          <span className="ad-agency-badge-text">Earn {card.badge} back on your yearly adspend</span>
        </div>
      )}

      {(card.id === 11 || card.id === 12 || card.id === 13 || card.id === 14 || card.id === 15 || card.id === 16) && (
        <div className="ad-agency-feature-badge ad-agency-simple-badge-new">
          <CheckCircle2 size={16} color="#4ade80" />
          <span className="ad-agency-badge-text">{card.badgeLabel}</span>
          {card.badge && <span className="ad-agency-badge-value-small">{card.badge}</span>}
        </div>
      )}
    </div>
  );
}

function CardsCarousel({ cards }) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const [direction, setDirection] = useState('right');
  const [transformValue, setTransformValue] = useState(0);
  const autoPlayRef = useRef(null);
  const [cardsPerView, setCardsPerView] = useState(3);
  const [visibleGroup, setVisibleGroup] = useState([]);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkScreenSize = () => {
      const width = window.innerWidth;
      if (width <= 768) {
        setCardsPerView(1);
        setIsMobile(true);
      } else if (width <= 1024) {
        setCardsPerView(2);
        setIsMobile(false);
      } else {
        setCardsPerView(3);
        setIsMobile(false);
      }
    };

    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);
    
    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  const updateVisibleGroup = useCallback((startIndex) => {
    const group = [];
    for (let i = 0; i < cardsPerView; i++) {
      const cardIndex = (startIndex + i) % cards.length;
      group.push(cards[cardIndex]);
    }
    setVisibleGroup(group);
  }, [cards, cardsPerView]);

  const nextSlide = useCallback(() => {
    if (isAnimating) return;
    setIsAnimating(true);
    setDirection('right');
    setTransformValue(-100);
    
    setTimeout(() => {
      setCurrentIndex((prev) => {
        const newIndex = (prev + cardsPerView) % cards.length;
        updateVisibleGroup(newIndex);
        return newIndex;
      });
      setTransformValue(0);
      setTimeout(() => setIsAnimating(false), 50);
    }, 300);
  }, [isAnimating, cardsPerView, cards.length, updateVisibleGroup]);

  const prevSlide = useCallback(() => {
    if (isAnimating) return;
    setIsAnimating(true);
    setDirection('left');
    setTransformValue(100);
    
    setTimeout(() => {
      setCurrentIndex((prev) => {
        const newIndex = (prev - cardsPerView + cards.length) % cards.length;
        updateVisibleGroup(newIndex);
        return newIndex;
      });
      setTransformValue(0);
      setTimeout(() => setIsAnimating(false), 50);
    }, 300);
  }, [isAnimating, cardsPerView, cards.length, updateVisibleGroup]);

  const goToSlide = useCallback((index) => {
    if (isAnimating || index === currentIndex) return;
    setIsAnimating(true);
    const direction = index > currentIndex ? 'right' : 'left';
    setDirection(direction);
    setTransformValue(direction === 'right' ? -100 : 100);
    
    setTimeout(() => {
      setCurrentIndex(index);
      updateVisibleGroup(index);
      setTransformValue(0);
      setTimeout(() => setIsAnimating(false), 50);
    }, 300);
    
    if (autoPlayRef.current) {
      clearInterval(autoPlayRef.current);
      autoPlayRef.current = setInterval(() => {
        nextSlide();
      }, 5000);
    }
  }, [isAnimating, currentIndex, updateVisibleGroup, nextSlide]);

  useEffect(() => {
    updateVisibleGroup(currentIndex);
    
    autoPlayRef.current = setInterval(() => {
      nextSlide();
    }, 5000);
    
    return () => {
      if (autoPlayRef.current) {
        clearInterval(autoPlayRef.current);
      }
    };
  }, [currentIndex, cardsPerView, updateVisibleGroup, nextSlide]);

  const getActiveDotIndex = () => {
    if (isMobile) {
      return Math.floor(currentIndex);
    } else {
      return Math.floor(currentIndex / cardsPerView);
    }
  };

  const getTotalDots = () => {
    if (isMobile) {
      return cards.length; 
    } else {
      return Math.ceil(cards.length / cardsPerView); 
    }
  };

  const handleDotClick = (dotIndex) => {
    if (isMobile) {
      goToSlide(dotIndex);
    } else {
      const targetIndex = dotIndex * cardsPerView;
      if (targetIndex < cards.length) {
        goToSlide(targetIndex);
      }
    }
  };

  const activeDot = getActiveDotIndex();
  const totalDots = getTotalDots();

  return (
    <div className="ad-agency-carousel-container">
      <div className="ad-agency-carousel-header">
        <div className="ad-agency-carousel-title-wrapper">
          <h3 className="ad-agency-carousel-title">Agency Account Features</h3>
        </div>
        <div className="ad-agency-carousel-controls">
          <button 
            className="ad-agency-carousel-nav-btn" 
            onClick={prevSlide}
            disabled={isAnimating}
          >
            <ChevronLeft size={20} />
          </button>
          <button 
            className="ad-agency-carousel-nav-btn" 
            onClick={nextSlide}
            disabled={isAnimating}
          >
            <ChevronRight size={20} />
          </button>
        </div>
      </div>
      
      <div className="ad-agency-carousel-track-wrapper">
        <div 
          className={`ad-agency-carousel-track ${isAnimating ? `animating-${direction}` : ''}`}
          style={{ 
            transform: `translateX(${transformValue}%)`,
            transition: isAnimating ? 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)' : 'none'
          }}
        >
          {visibleGroup.map((card, idx) => (
            <div 
              key={card.id} 
              className="ad-agency-carousel-item"
              style={{ animationDelay: `${idx * 100}ms` }}
            >
              <FeatureCard card={card} index={idx} isVisible={true} />
            </div>
          ))}
        </div>
      </div>
      
      <div className="ad-agency-carousel-dots">
        {Array.from({ length: totalDots }).map((_, idx) => {
          const isActive = idx === activeDot;
          
          return (
            <button
              key={idx}
              className={`ad-agency-carousel-dot ${isActive ? 'active' : ''}`}
              onClick={() => handleDotClick(idx)}
            />
          );
        })}
      </div>
    </div>
  );
}

export default function AgencyAccSection() {
  const [heroVisible, setHeroVisible] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setHeroVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <section ref={sectionRef} className="ad-agency-container">
      <div className="ad-agency-mesh-bg" />
      <div className="ad-agency-noise-overlay" />
      <div className="ad-agency-glow-orb" />
      <div className="ad-agency-blob ad-agency-blob-1" />
      <div className="ad-agency-blob ad-agency-blob-2" />
      <div className="ad-agency-blob ad-agency-blob-3" />

      <div className="ad-agency-wrapper">
        <div className="ad-agency-grid">
          <div className="ad-agency-left-col">
            <div className={`ad-agency-fade-up ${heroVisible ? "visible" : ""}`}>
              <div className="ad-agency-stat-chip">
                <div className="ad-agency-live-dot" />
                <span>40+ active agency accounts</span>
              </div>
            </div>

            <div className={`ad-agency-fade-up ${heroVisible ? "visible" : ""}`}>
              <h1 className="ad-agency-headline">
                Fast-track scale with{" "}
                <span className="ad-agency-accent-word">whitelisted</span>{" "}
                advertising accounts on Meta
              </h1>
            </div>

            <div className={`ad-agency-fade-up ${heroVisible ? "visible" : ""}`}>
              <p className="ad-agency-subtext">
                Skyrocket your business success with dedicated support, cashback, fewer bans,
                fewer restrictions, and cost-effective results.
              </p>
            </div>

            <div className={`ad-agency-fade-up ${heroVisible ? "visible" : ""}`}>
              <div className="ad-agency-cta-row">
                <button className="ad-agency-btn-primary" onClick={() => window.location.href = "/contact-us"}>
                  Get started now <ChevronRight size={18} />
                </button>
                <a className="ad-agency-btn-secondary" href="https://wa.me/919582795505?text=Hii">
                  Contact sales <ArrowUpRight size={16} />
                </a>
              </div>
            </div>
            
            {/* Trust indicators 
            <div className={`ad-agency-trust-row ${heroVisible ? "visible" : ""}`}>
              <div className="ad-agency-trust-item">
                <div className="ad-agency-stars">★★★★★</div>
                <span>4.9/5 from 500+ agencies</span>
              </div>
              <div className="ad-agency-trust-item">
                <Shield size={14} />
                <span>Meta Preferred Partner</span>
              </div>
            </div>
            */}
          </div>

          <div className="ad-agency-right-col">
            <div className="ad-agency-mockups-wrapper">
              <div className="ad-agency-mockups-col ad-agency-mockups-col-top">
                {adMockups.slice(0, 2).map((mock, i) => (
                  <AdMockupCard key={mock.id} mock={mock} index={i} />
                ))}
              </div>
              <div className="ad-agency-mockups-col ad-agency-mockups-col-middle">
                {adMockups.slice(2, 4).map((mock, i) => (
                  <AdMockupCard key={mock.id} mock={mock} index={i + 2} />
                ))}
              </div>
              <div className="ad-agency-mockups-col ad-agency-mockups-col-bottom">
                <AdMockupCard mock={adMockups[4]} index={4} />

                <div className={`ad-agency-stats-card ${heroVisible ? "visible" : ""}`}>
                  <div className="ad-agency-stats-label">Average ROAS this month</div>
                  <div className="ad-agency-stats-value">7.8<span className="ad-agency-stats-symbol">×</span></div>
                  <div className="ad-agency-stats-trend">
                    <TrendingUp size={12} /> +22% vs last month
                  </div>
                </div>
              </div>
            </div>

            <div className="ad-agency-floating-badge ad-agency-floating-badge-top">
              <Lock size={14} /> Whitelisted & Verified
            </div>

            <div className="ad-agency-floating-badge ad-agency-floating-badge-bottom">
              <CreditCard size={14} /> $2.4M+ cashback paid
            </div>

            <div className="ad-agency-floating-badge ad-agency-floating-badge-right">
              <Gauge size={14} /> 99.9% uptime
            </div>

            <div className="ad-agency-floating-badge ad-agency-floating-badge-left">
              <Users size={14} /> 10k+ campaigns live
            </div>
          </div>
        </div>
        <CardsCarousel cards={cards} />
      </div>
    </section>
  );
}